import { isAirportClient } from '~/config/airports.config';
import { SUPPORTED_GAMEPAD_LANGS } from '~/config/languages';

/**
 * Réglage « Laisser chaque joueur choisir sa langue » d'un établissement, tel que le
 * téléphone le connaît : `true` / `false` une fois lu, `undefined` tant qu'il n'est
 * pas lu (ou si la lecture a échoué). Source : `clients/{id}/public/gamepadSettings`
 * (cf. services/playerLanguage), le téléphone ne pouvant pas lire `clients/{id}`.
 */
export type VenueLanguagePicker = boolean | undefined;

/**
 * Le joueur peut-il choisir sa langue sur ce téléphone ? Toujours en aéroport
 * (passagers internationaux, historique) ; ailleurs seulement si l'établissement a
 * allumé l'option. Tant que le réglage n'est pas lu : non (aucun champ en plus).
 */
export function canPickLanguage(clientID: string | null | undefined, venueAllows: VenueLanguagePicker): boolean {
    return isAirportClient(clientID ?? undefined) || venueAllows === true;
}

/** Écran du téléphone où les drapeaux pourraient s'afficher. */
export type LanguagePickerScreen = 'profile' | 'rejoin' | 'inGame';

/**
 * Drapeaux affichés sur CET écran ? Écran du pseudo : partout où le choix est
 * proposé (aéroports compris, comme avant). « Salut X » (habitués) et réglages en
 * partie : seulement les lieux qui ont allumé l'option — les aéroports gardent leur
 * affichage d'origine (décision Victorien 16/10).
 */
export function showsLanguagePicker(
    clientID: string | null | undefined,
    venueAllows: VenueLanguagePicker,
    screen: LanguagePickerScreen
): boolean {
    if (screen === 'profile') return canPickLanguage(clientID, venueAllows);
    return !isAirportClient(clientID ?? undefined) && venueAllows === true;
}

/**
 * Faut-il appliquer la langue que le joueur a choisie sur ce téléphone ?
 *
 * Le choix est enregistré sur le téléphone, avec le lieu où il a été fait. On ne
 * l'applique que là où le choix est proposé : un établissement qui n'a pas l'option
 * (ou qui l'a éteinte) affiche la langue du téléphone, sinon le joueur resterait
 * coincé dans une langue sans aucun moyen d'en changer. Le choix n'est pas effacé :
 * il revient dès que le joueur retourne dans un lieu qui propose l'option.
 *
 * Réglage pas encore lu (ou illisible) : on décide sans attendre le réseau, d'après le
 * lieu du choix — appliqué seulement si c'est ici. Ainsi la langue ne saute pas à
 * l'arrivée dans un bar sans l'option (le cas courant).
 */
export function honorsSavedLanguage(
    clientID: string | null | undefined,
    venueAllows: VenueLanguagePicker,
    savedVenue: string | null | undefined
): boolean {
    if (canPickLanguage(clientID, venueAllows)) return true;
    // Lien sans établissement (outil, vieux lien) : comportement d'avant, le choix s'applique.
    if (!clientID) return true;
    if (venueAllows === false) return false;
    return savedVenue === clientID;
}

/**
 * Langue du téléphone du joueur : choix enregistré (s'il s'applique ici) > langue du
 * navigateur > langue de l'écran (URL) > anglais.
 */
export function resolveGamepadLanguage({
    saved,
    honorSaved,
    browser,
    url,
}: {
    saved: string | null | undefined;
    honorSaved: boolean;
    browser: string | null | undefined;
    url: string | null | undefined;
}): string {
    if (honorSaved && saved && SUPPORTED_GAMEPAD_LANGS.includes(saved)) return saved;

    const browserLang = browser?.substring(0, 2);
    if (browserLang && SUPPORTED_GAMEPAD_LANGS.includes(browserLang)) return browserLang;

    if (url && SUPPORTED_GAMEPAD_LANGS.includes(url)) return url;

    return 'en';
}

/** Code à présélectionner dans le sélecteur de drapeaux (langue i18n courante, sinon anglais). */
export function selectedGamepadLanguage(i18nLanguage: string | null | undefined): string {
    const current = i18nLanguage?.substring(0, 2);
    return current && SUPPORTED_GAMEPAD_LANGS.includes(current) ? current : 'en';
}
